import {
  faChartPie,
  faClipboardQuestion,
  faCodeBranch,
  faCubes,
  faLaptopCode,
  faLayerGroup,
  faMagnifyingGlass,
  faMagnifyingGlassChart,
  faOtter,
  faPenNib,
  faPeopleArrows,
  faPersonCircleQuestion,
  faRainbow,
  faSitemap,
  faUsersViewfinder,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import {
  faComments,
  faHandshake,
  faObjectUngroup,
} from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Skill, SkillTag } from "../../client";
import IconGrid, { type IconItem } from "../../components/IconGrid";
import EditorSkillTagButton from "../../components/atoms/EditorSkillTagButton";
import { searchSkills } from "../../requests";

/**
 * SkillPicker — a rewrite of the old TagsInputDialog's core, with a clean
 * callback API (no relay-array). Search the skill catalogue (real icons), or
 * create a custom skill by typing a name and picking a symbol. The parent owns
 * persistence and dedupe; this component only emits add/remove events.
 */

const CUSTOM_ICONS: IconItem[] = [
  { name: "rainbow", icon: faRainbow },
  { name: "cubes", icon: faCubes },
  { name: "pen-nib", icon: faPenNib },
  { name: "wand-magic-sparkles", icon: faWandMagicSparkles },
  { name: "object-ungroup", icon: faObjectUngroup },
  { name: "otter", icon: faOtter },
  { name: "laptop-code", icon: faLaptopCode },
  { name: "magnifying-glass-chart", icon: faMagnifyingGlassChart },
  { name: "chart-pie", icon: faChartPie },
  { name: "code-branch", icon: faCodeBranch },
  { name: "site-map", icon: faSitemap },
  { name: "layer-group", icon: faLayerGroup },
  { name: "people-arrows", icon: faPeopleArrows },
  { name: "comments", icon: faComments },
  { name: "clipboard-question", icon: faClipboardQuestion },
  { name: "person-circle-question", icon: faPersonCircleQuestion },
  { name: "users-viewfinder", icon: faUsersViewfinder },
  { name: "handshake", icon: faHandshake },
];

interface Props {
  addedSkills: SkillTag[];
  onAddSkill: (tag: SkillTag) => void;
  onRemoveSkill: (name: string) => void;
  /** Optional quick-add chips (e.g. assignment-gap "missing" skills). */
  suggestedSkills?: SkillTag[];
  suggestedLabel?: string;
}

export default function SkillPicker({
  addedSkills,
  onAddSkill,
  onRemoveSkill,
  suggestedSkills = [],
  suggestedLabel = "Suggested skills",
}: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Skill[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the search on mount (the drawer mounts fresh each open).
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const addedNames = useMemo(
    () => new Set(addedSkills.map((s) => s.name.toLowerCase())),
    [addedSkills],
  );

  // Debounced catalogue search (mirrors the old dialog's live search).
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      return;
    }
    const timer = setTimeout(() => {
      searchSkills(q)
        .then(setResults)
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [query]);

  const visibleResults = results.filter(
    (s) => s.name && !addedNames.has(s.name.toLowerCase()),
  );

  const trimmed = query.trim();
  const exactExists =
    trimmed !== "" &&
    (addedNames.has(trimmed.toLowerCase()) ||
      results.some((s) => s.name?.toLowerCase() === trimmed.toLowerCase()));

  const add = (tag: SkillTag) => {
    if (addedNames.has(tag.name.toLowerCase())) return;
    onAddSkill(tag);
  };

  const visibleSuggested = suggestedSkills.filter(
    (s) => !addedNames.has(s.name.toLowerCase()),
  );

  return (
    <div className="flex flex-col gap-5">
      {visibleSuggested.length > 0 && (
        <div className="flex flex-col gap-2 rounded-lg border-2 border-dashed border-red-2 bg-red-1 p-2">
          <span className="text-xs text-gray-6">
            {suggestedLabel} (tap to add)
          </span>
          <div className="flex flex-row flex-wrap gap-1">
            {visibleSuggested.map((skill) => (
              <button
                key={skill.name}
                type="button"
                className="inline-block"
                onClick={() => add(skill)}
              >
                <EditorSkillTagButton
                  profileSkillTag={skill}
                  text={skill.name}
                  disableEditing={true}
                  isStatic={true}
                  keepLeftPadding={true}
                  displayInfoOnHover={true}
                  handleClick={() => {}}
                />
              </button>
            ))}
          </div>
        </div>
      )}
      {addedSkills.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-5">
            Added ({addedSkills.length})
          </span>
          <div className="flex flex-row flex-wrap gap-2">
            {addedSkills.map((skill) => (
              <button
                key={skill.name}
                type="button"
                className="inline-block"
                onClick={() => onRemoveSkill(skill.name)}
                title="Remove"
              >
                <EditorSkillTagButton
                  profileSkillTag={skill}
                  text={skill.name}
                  disableEditing={true}
                  isStatic={true}
                  keepLeftPadding={true}
                  handleClick={() => {}}
                />
              </button>
            ))}
          </div>
          <span className="text-xs text-gray-5">Click one to remove.</span>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-5">
          Add skill
        </span>
        <div className="flex items-center gap-2 rounded-lg border border-gray-3 px-3 py-2 focus-within:border-op-blue">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="h-3.5 w-3.5 text-gray-5"
          />
          <input
            ref={inputRef}
            value={query}
            placeholder="Search for a skill…"
            onChange={(e) => setQuery(e.target.value)}
            className="w-full text-sm outline-none"
          />
        </div>
      </div>

      {visibleResults.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-xs text-gray-6">Pick an existing skill</span>
          <div className="flex flex-row flex-wrap gap-2">
            {visibleResults.map((skill) => (
              <button
                key={skill.name}
                type="button"
                className="inline-block"
                onClick={() =>
                  add({
                    name: skill.name ?? "",
                    customIconName: skill.iconName ?? "",
                  })
                }
              >
                <EditorSkillTagButton
                  profileSkillTag={{
                    name: skill.name ?? "",
                    customIconName: skill.iconName ?? "",
                  }}
                  text={skill.name ?? ""}
                  disableEditing={true}
                  isStatic={true}
                  keepLeftPadding={true}
                  handleClick={() => {}}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {trimmed !== "" && !exactExists && (
        <div className="flex flex-col gap-2">
          <span className="text-xs text-gray-6">
            …or pick a symbol to create <b>{trimmed}</b> as a custom skill
          </span>
          <IconGrid
            titles={["Design", "Code & data", "People"]}
            icons={CUSTOM_ICONS}
            itemsPerRow={6}
            onIconClick={(iconName) => {
              add({ name: trimmed, customIconName: iconName });
              setQuery("");
            }}
          />
        </div>
      )}
    </div>
  );
}
